import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  DatePicker,
  Select,
  Space,
  message,
  Divider,
  InputNumber,
  Tag,
  Input,
  Drawer,
  Descriptions,
  Typography,
  Alert,
  Spin,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  LockOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  rentals,
  customers as custApi,
  products as prodApi,
  inventory,
} from "../api";
import PageHeader from "../components/common/PageHeader";
import StatusBadge from "../components/common/StatusBadge";
import { fmtDate } from "../utils/format";
import dayjs from "dayjs";

const { Text } = Typography;
const SECRET_KEY = "admin123";

export default function Rentals() {
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewRec, setViewRec] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [allCusts, setAllCusts] = useState([]);
  const [allProds, setAllProds] = useState([]);
  const [availQty, setAvailQty] = useState({});
  const [lines, setLines] = useState([{ productId: null, qtyRented: 1 }]);
  const [advanceAmount, setAdvanceAmount] = useState(0); // total advance for this rental
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [secretModal, setSecretModal] = useState(null);
  const [secretInput, setSecretInput] = useState("");
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await rentals.list({ q, page, size: 20 });
      setData(r.data.data);
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    custApi.all().then((r) => setAllCusts(r.data.data));
    prodApi.all().then((r) => {
      const rentable = r.data.data.filter(
        (p) => p.productType === "RENT" || p.productType === "BOTH",
      );
      setAllProds(rentable);
    });
    inventory.stock().then((r) => {
      const qtyMap = {};
      (r.data.data.rentalProducts || []).forEach((p) => {
        qtyMap[p.productId] = Number(p.inStorage ?? 0);
      });
      setAvailQty(qtyMap);
    });
  }, []);

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    // FIX: use DatePicker with showTime — store full datetime
    form.setFieldsValue({ rentalDateTime: dayjs() });
    setLines([{ productId: null, qtyRented: 1 }]);
    setAdvanceAmount(0);
    setModal(true);
  };

  const openEdit = async (rec) => {
    // FIX: always fetch fresh record
    try {
      const res = await rentals.get(rec.id);
      const fresh = res.data.data;
      setEditing(fresh);
      form.setFieldsValue({
        customerId: fresh.customer?.id,
        rentalDateTime: fresh.rentalDateTime
          ? dayjs(fresh.rentalDateTime)
          : dayjs(fresh.rentalDate),
        notes: fresh.notes,
      });
      setLines(
        (fresh.items || []).map((i) => ({
          productId: i.product?.id,
          qtyRented: Number(i.qtyRented),
        })),
      );
      setAdvanceAmount(Number(fresh.advanceAmount || 0));
      setModal(true);
    } catch {
      message.error("Failed to load rental details");
    }
  };

  const openView = async (rec) => {
    setViewRec({ id: rec.id, rentalNo: rec.rentalNo, _loading: true });
    setViewLoading(true);
    try {
      const res = await rentals.get(rec.id);
      setViewRec(res.data.data);
    } catch {
      message.error("Failed to load rental");
      setViewRec(null);
    } finally {
      setViewLoading(false);
    }
  };

  const addLine = () => {
    const newLines = [...lines, { productId: null, qtyRented: 1 }];
    setLines(newLines);
  };

  const updateLine = (i, f, v) => {
    const l = [...lines];
    l[i][f] = v;
    setLines(l);
    // Recalculate suggested advance whenever product or qty changes
    recalcAdvance(l);
  };

  const recalcAdvance = async (currentLines) => {
    let total = 0;
    for (const line of currentLines) {
      if (!line.productId || !line.qtyRented) continue;
      try {
        const res = await prodApi.currentAdvance(line.productId);
        const adv = Number(res.data.data || 0);
        total += adv * Number(line.qtyRented);
      } catch {
        // If fetch fails, fall back to 0 for this line
      }
    }
    setAdvanceAmount(parseFloat(total.toFixed(2)));
  };
  const getMax = (productId) =>
    productId ? (availQty[productId] ?? 9999) : 9999;

  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const vals = await form.validateFields();

      // Duplicate product check
      const ids = lines.map((l) => l.productId).filter(Boolean);
      if (new Set(ids).size !== ids.length) {
        message.error("Each product can only appear once. Remove duplicates.");
        return;
      }

      for (const l of lines) {
        if (!l.productId) {
          message.error("Select a product for all lines");
          return;
        }
        const max = getMax(l.productId);
        if (l.qtyRented > max) {
          const prod = allProds.find((p) => p.id === l.productId);
          message.error(`${prod?.name}: only ${max} available`);
          return;
        }
      }
      const payload = {
        ...vals,
        rentalDate: vals.rentalDateTime.format("YYYY-MM-DD"),
        rentalDateTime: vals.rentalDateTime.format("YYYY-MM-DDTHH:mm:ss"),
        advanceAmount: advanceAmount,
        items: lines.map((l) => ({
          productId: l.productId,
          qtyRented: l.qtyRented,
        })),
      };
      if (editing?.id) {
        await rentals.update(editing.id, payload);
        message.success("Rental updated");
      } else {
        await rentals.create(payload);
        message.success("Rental created");
      }
      setModal(false);
      load();
      inventory.stock().then((r) => {
        const qtyMap = {};
        (r.data.data.rentalProducts || []).forEach((p) => {
          qtyMap[p.productId] = Number(p.inStorage ?? 0);
        });
        setAvailQty(qtyMap);
      });
    } catch {
      message.error("Please check the form");
    } finally {
      setSaving(false);
    }
  };

  const promptSecret = (action, record) => {
    // FIX: block edit/delete immediately if rental is locked
    if (record.locked) {
      message.warning(
        "This rental is locked — a return has already been started. It cannot be edited or deleted.",
      );
      return;
    }
    setSecretInput("");
    setSecretModal({ action, record });
  };

  const confirmSecret = async () => {
    if (secretInput !== SECRET_KEY) {
      message.error("Incorrect secret key");
      return;
    }
    const { action, record } = secretModal;
    setSecretModal(null);
    if (action === "edit") {
      await openEdit(record);
    } else if (action === "delete") {
      try {
        await rentals.delete(record.id);
        message.success("Rental deleted");
        load();
      } catch (err) {
        message.error(err?.response?.data?.message || "Cannot delete");
      }
    }
  };

  const cols = [
    {
      title: "Rental No",
      dataIndex: "rentalNo",
      render: (v) => <span className="mono text-info">{v}</span>,
    },
    {
      title: "Customer",
      dataIndex: ["customer", "name"],
      render: (v) => <strong>{v ?? "—"}</strong>,
    },
    { title: "Date", dataIndex: "rentalDate", render: fmtDate },
    {
      title: "Item Status",
      dataIndex: "billStatus",
      render: (v) => <StatusBadge status={v || "PENDING"} />,
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      render: (v) => <StatusBadge status={v || "PENDING"} />,
    },
    {
      title: "Net Bill",
      dataIndex: "netBill",
      render: (v) =>
        v > 0 ? (
          <span className="mono" style={{ fontWeight: 600 }}>
            ₹{Number(v).toFixed(2)}
          </span>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>—</span>
        ),
    },
    {
      title: "Deferred",
      dataIndex: "deferredAmount",
      render: (v) =>
        v > 0 ? (
          <span className="mono" style={{ color: "#f5222d", fontWeight: 600 }}>
            ₹{Number(v).toFixed(2)}
          </span>
        ) : (
          <span style={{ color: "#52c41a" }}>—</span>
        ),
    },
    {
      title: "Items",
      dataIndex: "items",
      render: (items) =>
        (items || []).map((i) => (
          <Tag key={i.id} color="purple">
            {i.product?.name} × {i.qtyRented}
            {i.qtyReturned > 0 && (
              <span style={{ color: "#888" }}> (ret: {i.qtyReturned})</span>
            )}
          </Tag>
        )),
    },
    {
      title: "Actions",
      key: "a",
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openView(r)}
          >
            View
          </Button>
          {/* FIX: show lock icon and disabled state if rental is locked */}
          <Tooltip title={r.locked ? "Locked — return in progress" : "Edit"}>
            <Button
              size="small"
              icon={r.locked ? <StopOutlined /> : <LockOutlined />}
              disabled={r.locked}
              onClick={() => !r.locked && promptSecret("edit", r)}
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title={r.locked ? "Locked — cannot delete" : "Delete"}>
            <Button
              size="small"
              icon={r.locked ? <StopOutlined /> : <LockOutlined />}
              danger
              disabled={r.locked}
              onClick={() => !r.locked && promptSecret("delete", r)}
            >
              Delete
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Rentals"
        subtitle="Issue items to customers on rent"
        actions={[
          <Input.Search
            key="s"
            placeholder="Search..."
            style={{ width: 200 }}
            onSearch={setQ}
            allowClear
          />,
          <Button
            key="a"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openNew}
          >
            New Rental
          </Button>,
        ]}
      />

      <Table
        scroll={{ x: "max-content" }}
        columns={cols}
        dataSource={data.content}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          total: data.totalElements,
          pageSize: 20,
          current: page + 1,
          onChange: (p) => setPage(p - 1),
        }}
      />

      {/* New / Edit Modal */}
      <Modal
        open={modal}
        title={editing?.id ? "Edit Rental" : "New Rental"}
        onCancel={() => setModal(false)}
        onOk={onSave}
        okButtonProps={{ loading: saving, disabled: saving }}        width={680}
        okText={editing?.id ? "Update" : "Create Rental"}
      >
        <Form form={form} layout="vertical">
          <Space style={{ width: "100%" }} align="start">
            <Form.Item
              name="customerId"
              label="Customer"
              rules={[{ required: true }]}
              style={{ minWidth: 260 }}
            >
              <Select
                showSearch
                placeholder="Select customer"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={allCusts.map((c) => ({
                  value: c.id,
                  label: `${c.name} (${c.phone})`,
                }))}
              />
            </Form.Item>
            {/* FIX: DatePicker with showTime to capture exact issue time for billing */}
            <Form.Item
              name="rentalDateTime"
              label="Issue Date & Time"
              rules={[{ required: true }]}
            >
              <DatePicker showTime format="YYYY-MM-DD HH:mm" />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes">
            <Input />
          </Form.Item>
        </Form>

        <Divider style={{ borderColor: "var(--border)" }}>
          Items to Rent
        </Divider>

        {lines.map((l, i) => {
          const max = getMax(l.productId);
          const over = l.productId && l.qtyRented > max;
          // Disable products already selected in other lines
          const otherSelected = lines
            .filter((_, idx) => idx !== i)
            .map((r) => r.productId)
            .filter(Boolean);
          return (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                marginBottom: 10,
              }}
            >
              <Select
                showSearch
                placeholder="Product"
                style={{ width: 260 }}
                value={l.productId}
                onChange={(v) => updateLine(i, "productId", v)}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                options={allProds.map((p) => ({
                  value: p.id,
                  label: `${p.name} (${p.unit}) — avail: ${availQty[p.id] ?? "?"}`,
                  disabled: otherSelected.includes(p.id),
                }))}
              />
              <div>
                <InputNumber
                  min={0.1}
                  step={0.5}
                  max={max}
                  value={l.qtyRented}
                  onChange={(v) => updateLine(i, "qtyRented", v)}
                  style={{ width: 110, borderColor: over ? "red" : undefined }}
                />
                {l.productId && (
                  <div
                    style={{
                      fontSize: 11,
                      color: over ? "red" : "var(--text-muted)",
                      marginTop: 2,
                    }}
                  >
                    {over ? `⚠ Exceeds available (${max})` : `Max: ${max}`}
                  </div>
                )}
              </div>
              {lines.length > 1 && (
                <Button
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  onClick={() => setLines(lines.filter((_, idx) => idx !== i))}
                />
              )}
            </div>
          );
        })}
        <Button icon={<PlusOutlined />} size="small" onClick={addLine}>
          Add Item
        </Button>

        {/* Advance section */}
        <Divider
          style={{ borderColor: "var(--border)", margin: "16px 0 10px" }}
        >
          Advance Collection
        </Divider>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--text-muted)",
                marginBottom: 4,
                fontFamily: "var(--font-mono)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Advance Amount (₹)
            </div>
            <InputNumber
              value={advanceAmount}
              min={0}
              prefix="₹"
              step={10}
              style={{ width: "100%" }}
              onChange={(v) => setAdvanceAmount(v ?? 0)}
            />
          </div>
          <div
            style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 16 }}
          >
            Auto-calculated from default advance × qty. Edit if needed.
          </div>
        </div>
        {advanceAmount > 0 && (
          <div
            style={{
              marginTop: 8,
              padding: "6px 12px",
              background: "rgba(63,185,80,0.1)",
              borderRadius: 6,
              border: "1px solid var(--success)",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--success)",
            }}
          >
            ₹{advanceAmount.toFixed(2)} will be recorded as advance payment on
            save.
          </div>
        )}
      </Modal>

      {/* Secret Key Modal */}
      <Modal
        open={!!secretModal}
        title={
          <span>
            <LockOutlined style={{ marginRight: 8 }} />
            Enter Secret Key
          </span>
        }
        onCancel={() => setSecretModal(null)}
        onOk={confirmSecret}
        okText="Confirm"
        okButtonProps={{ danger: secretModal?.action === "delete" }}
      >
        <p style={{ marginBottom: 12, color: "var(--text-muted)" }}>
          {secretModal?.action === "delete"
            ? "This will permanently delete the rental record."
            : "Enter secret key to edit this rental."}
        </p>
        <Input.Password
          placeholder="Secret key"
          prefix={<LockOutlined />}
          value={secretInput}
          onChange={(e) => setSecretInput(e.target.value)}
          onPressEnter={confirmSecret}
          autoFocus
        />
      </Modal>

      {/* View Drawer */}
      <Drawer
        title={`Rental: ${viewRec?.rentalNo}`}
        open={!!viewRec}
        onClose={() => setViewRec(null)}
        width={520}
      >
        {viewRec?._loading || viewLoading ? (
          <div style={{ textAlign: "center", paddingTop: 60 }}>
            <Spin size="large" />
          </div>
        ) : (
          viewRec && (
            <>
              {viewRec.locked && (
                <Alert
                  type="warning"
                  style={{ marginBottom: 12 }}
                  message="🔒 This rental is locked — a return has been started. Edit and Delete are disabled."
                />
              )}
              <Descriptions
                bordered
                size="small"
                column={1}
                style={{ marginBottom: 16 }}
              >
                <Descriptions.Item label="Rental No">
                  {viewRec.rentalNo}
                </Descriptions.Item>
                <Descriptions.Item label="Customer">
                  <strong>{viewRec.customer?.name}</strong>
                  {viewRec.customer?.phone && (
                    <Text type="secondary"> · {viewRec.customer.phone}</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Issue Date">
                  {fmtDate(viewRec.rentalDate)}
                </Descriptions.Item>
                <Descriptions.Item label="Issue Time">
                  {viewRec.rentalDateTime
                    ? dayjs(viewRec.rentalDateTime).format("HH:mm")
                    : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Item Status">
                  <StatusBadge status={viewRec.billStatus || "PENDING"} />
                </Descriptions.Item>
                <Descriptions.Item label="Payment Status">
                  <StatusBadge status={viewRec.paymentStatus || "PENDING"} />
                </Descriptions.Item>
                <Descriptions.Item label="Total Bill">
                  <span className="mono">
                    ₹{Number(viewRec.totalBill || 0).toFixed(2)}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Net Bill (after offer)">
                  <span className="mono" style={{ fontWeight: 700 }}>
                    ₹{Number(viewRec.netBill || 0).toFixed(2)}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Deferred (Outstanding)">
                  {Number(viewRec.deferredAmount || 0) > 0 ? (
                    <span
                      className="mono"
                      style={{ color: "#f5222d", fontWeight: 600 }}
                    >
                      ₹{Number(viewRec.deferredAmount).toFixed(2)}
                    </span>
                  ) : (
                    <Tag color="green">Fully Paid</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Advance Collected">
                  {Number(viewRec.advanceAmount || 0) > 0 ? (
                    <span
                      className="mono"
                      style={{ color: "var(--success)", fontWeight: 600 }}
                    >
                      ₹{Number(viewRec.advanceAmount).toFixed(2)}
                    </span>
                  ) : (
                    <span style={{ color: "var(--text-muted)" }}>None</span>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Notes">
                  {viewRec.notes || "—"}
                </Descriptions.Item>
              </Descriptions>

              <Divider
                orientation="left"
                style={{ color: "var(--success,#52c41a)" }}
              >
                Rental Items
              </Divider>
              <Table
                scroll={{ x: "max-content" }}
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={viewRec.items || []}
                columns={[
                  {
                    title: "Product",
                    dataIndex: ["product", "name"],
                    render: (v) => <span className="mono">{v}</span>,
                  },
                  {
                    title: "Unit",
                    dataIndex: ["product", "unit"],
                    render: (v) => <Tag>{v}</Tag>,
                  },
                  { title: "Rented", dataIndex: "qtyRented" },
                  { title: "Returned", dataIndex: "qtyReturned" },
                  {
                    title: "Outstanding",
                    render: (_, r) => {
                      const out = Number(r.qtyRented) - Number(r.qtyReturned);
                      return (
                        <Tag color={out > 0 ? "orange" : "green"}>
                          {out.toFixed(2)}
                        </Tag>
                      );
                    },
                  },
                ]}
              />
            </>
          )
        )}
      </Drawer>
    </div>
  );
}